import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { formatDate, isToday, isFutureDate, parseDate, isPastOrPresent, getTodayDate } from '../utils/dateUtils';
import QuoteInteraction from './QuoteInteraction';

type Quote = {
  id: number;
  text: string;
  author: string;
  date: string;
};

type FavoriteQuote = Quote & {
  collectedAt: number;
  insight?: string;
};

type QuoteCalendarProps = {
  quotes: Quote[];
  todayQuote: Quote | null;
  onLike: (quote: Quote) => void;
  favorites: FavoriteQuote[];
  onUpdateInsight: (id: number, insight: string) => void;
};

const QuoteCalendar: React.FC<QuoteCalendarProps> = ({ quotes, todayQuote, onLike, favorites, onUpdateInsight }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  const handleDateChange = useCallback((newDate: Date) => {
    const formattedDate = formatDate(newDate);
    const quote = quotes.find(q => q.date === formattedDate);

    if (isPastOrPresent(newDate)) {
      setCurrentDate(newDate);
      if (quote) {
        setSelectedQuote(quote);
      }
    }
  }, [quotes]);

  useEffect(() => {
    // Set today's date and quote on initial render
    const today = new Date();
    handleDateChange(today);
  }, [handleDateChange]);

  useEffect(() => {
    // Ensure today's quote is selected when the component mounts
    if (todayQuote && !selectedQuote) {
      setSelectedQuote(todayQuote);
    }
  }, [todayQuote, selectedQuote]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (calendarRef.current || calendarRef.current.contains(document.activeElement)) {
        const newDate = new Date(currentDate);
        let validDateFound = false;

        const moveDate = (direction: 'forward' | 'backward') => {
          while (!validDateFound) {
            if (direction === 'forward') {
              newDate.setDate(newDate.getDate() + 1);
            } else {
              newDate.setDate(newDate.getDate() - 1);
            }

            const formattedNewDate = formatDate(newDate);
            const quoteExists = quotes.some(q => q.date === formattedNewDate);
            
            if (isPastOrPresent(newDate) && quoteExists) {
              validDateFound = true;
            }

            // Prevent infinite loop if no valid date is found
            if (direction === 'forward' && isFutureDate(newDate)) break;
            if (direction === 'backward' && newDate < new Date(quotes[0].date)) break;
          }
        };

        switch (e.key) {
          case 'ArrowLeft':
            moveDate('backward');
            break;
          case 'ArrowRight':
            moveDate('forward');
            break;
          case 'ArrowUp':
            moveDate('backward');
            break;
          case 'ArrowDown':
            moveDate('forward');
            break;
        }

        if (validDateFound) {
          handleDateChange(newDate);
          setCurrentDate(newDate);
          e.preventDefault();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentDate, handleDateChange, quotes]);

  const renderCalendar = () => {    
    const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    const days = [];

    // Add empty cells for days before the start of the month
    const firstDayOfWeek = startDate.getDay();
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    for (let d = startDate; d <= endDate; d.setDate(d.getDate() + 1)) {
      const date = new Date(d);
      const formattedDate = formatDate(date);
      const quote = quotes.find(q => q.date === formattedDate);
      const isSelected = selectedQuote && selectedQuote.date === formattedDate;
      const isTodayDate = isToday(date);
      const isFuture = isFutureDate(date);
      const isDisabled = isFuture || !quote;

      days.push(
        <button
          key={formattedDate}
          onClick={() => !isDisabled && handleDateChange(date)}
          className={`calendar-day ${isSelected ? 'selected' : ''} ${isTodayDate ? 'today' : ''} ${quote ? 'has-quote' : ''} ${isFuture ? 'future' : ''} ${isDisabled ? 'disabled' : ''}`}
          disabled={isDisabled}
          tabIndex={isDisabled ? -1 : 0}
        >
          {date.getDate()}
        </button>
      );
    }

    return days;
  };

  return (
    <div className="flex flex-col lg:flex-row space-y-4 lg:space-y-0 lg:space-x-4">
      <div className="lg:w-1/2">
        <div className="flex justify-between items-center mb-4">
          <button
            onClick={() => handleDateChange(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))}
            className="p-2 rounded-full bg-gray-200 text-gray-600 hover:bg-indigo-500 hover:text-white transition-colors duration-200"
          >
            <ChevronLeft size={20} />
          </button>
          <h2 className="text-xl font-bold">
            {currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
          </h2>
          <button
            onClick={() => handleDateChange(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))}
            className="p-2 rounded-full bg-gray-200 text-gray-600 hover:bg-indigo-500 hover:text-white transition-colors duration-200"
          >
            <ChevronRight size={20} />
          </button>
        </div>
        <div ref={calendarRef} className="grid grid-cols-7 gap-1">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-center font-bold">{day}</div>
          ))}
          {renderCalendar()}
        </div>
      </div>
      <div className="lg:w-1/2">
        {selectedQuote && (
          <div className="bg-white rounded-lg shadow p-4">
            <QuoteInteraction
              quote={selectedQuote}
              onLike={onLike}
              favorites={favorites}
              onUpdateInsight={onUpdateInsight}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(QuoteCalendar);